import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';

function LocationManagement() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('locations'); // 'locations' or 'zones'
  const [locations, setLocations] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    capacity: '',
    manager: '',
    contact: '',
    active: true
  });
  const [zoneFormData, setZoneFormData] = useState({
    name: '',
    locationId: '',
    locationName: '',
    description: '',
    capacity: '',
    isDisplay: true,
    active: true
  });

  useEffect(() => {
    loadLocations();
    loadZones();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);

      // 1단계: 먼저 locations 데이터 로드
      const locationsRef = collection(db, 'locations');
      const snapshot = await getDocs(locationsRef);

      // 2단계: inventory 데이터를 한 번만 로드
      const inventoryRef = collection(db, 'inventory');
      const inventoryQuery = query(inventoryRef, where('status', '!=', '판매완료'));
      const inventorySnapshot = await getDocs(inventoryQuery);

      // 3단계: 위치별 재고 수 계산
      const inventoryCountByLocation = {};
      inventorySnapshot.docs.forEach(doc => {
        const location = doc.data().location;
        inventoryCountByLocation[location] = (inventoryCountByLocation[location] || 0) + 1;
      });

      // 4단계: locations 데이터와 재고 수 결합
      const locationList = snapshot.docs.map(docSnap => {
        const locationData = docSnap.data();
        return {
          id: docSnap.id,
          ...locationData,
          itemCount: inventoryCountByLocation[locationData.name] || 0,
          createdAt: locationData.createdAt?.toDate?.() || new Date(),
          order: locationData.order ?? 999 // order가 없으면 맨 뒤로
        };
      });

      // order 순으로 정렬 (order가 같으면 생성일 순)
      setLocations(locationList.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.createdAt - b.createdAt;
      }));
    } catch (error) {
      console.error('위치 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadZones = async () => {
    try {
      const zonesRef = collection(db, 'zones');
      const snapshot = await getDocs(zonesRef);

      // inventory에서 zone별 재고 수 계산
      const inventoryRef = collection(db, 'inventory');
      const inventoryQuery = query(inventoryRef, where('status', '!=', '판매완료'));
      const inventorySnapshot = await getDocs(inventoryQuery);

      const inventoryCountByZone = {};
      inventorySnapshot.docs.forEach(doc => {
        const zoneId = doc.data().zoneId;
        if (zoneId) {
          inventoryCountByZone[zoneId] = (inventoryCountByZone[zoneId] || 0) + 1;
        }
      });

      const zoneList = snapshot.docs.map(docSnap => {
        const zoneData = docSnap.data();
        return {
          id: docSnap.id,
          ...zoneData,
          itemCount: inventoryCountByZone[docSnap.id] || 0,
          createdAt: zoneData.createdAt?.toDate?.() || new Date(),
          order: zoneData.order ?? 999
        };
      });

      setZones(zoneList.sort((a, b) => {
        // 먼저 locationName으로 그룹화, 그 다음 order로 정렬
        if (a.locationName !== b.locationName) return a.locationName?.localeCompare(b.locationName) || 0;
        if (a.order !== b.order) return a.order - b.order;
        return a.createdAt - b.createdAt;
      }));
    } catch (error) {
      console.error('Zone 로드 오류:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingLocation) {
        // 수정
        const locationRef = doc(db, 'locations', editingLocation.id);
        await updateDoc(locationRef, {
          ...formData,
          capacity: formData.capacity ? parseInt(formData.capacity) : null,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        });
        alert('위치가 수정되었습니다.');
      } else {
        // 추가 - 새 위치는 맨 뒤에 추가 (가장 큰 order + 1)
        const maxOrder = locations.length > 0
          ? Math.max(...locations.map(loc => loc.order ?? 0)) + 1
          : 0;
        await addDoc(collection(db, 'locations'), {
          ...formData,
          capacity: formData.capacity ? parseInt(formData.capacity) : null,
          order: maxOrder,
          createdAt: serverTimestamp(),
          createdBy: user.email
        });
        alert('위치가 추가되었습니다.');
      }

      resetForm();
      loadLocations();
    } catch (error) {
      console.error('위치 저장 오류:', error);
      alert('위치 저장 중 오류가 발생했습니다.');
    }
  };

  const handleZoneSubmit = async (e) => {
    e.preventDefault();

    if (!zoneFormData.locationId) {
      alert('위치를 선택해주세요.');
      return;
    }

    try {
      const selectedLocation = locations.find(loc => loc.id === zoneFormData.locationId);

      if (editingZone) {
        const zoneRef = doc(db, 'zones', editingZone.id);
        await updateDoc(zoneRef, {
          name: zoneFormData.name,
          locationId: zoneFormData.locationId,
          locationName: selectedLocation?.name || '',
          description: zoneFormData.description,
          capacity: zoneFormData.capacity ? parseInt(zoneFormData.capacity) : null,
          isDisplay: zoneFormData.isDisplay,
          active: zoneFormData.active,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        });
        alert('Zone이 수정되었습니다.');
      } else {
        // 해당 위치의 zone 중 가장 큰 order + 1
        const locationZones = zones.filter(z => z.locationId === zoneFormData.locationId);
        const maxOrder = locationZones.length > 0
          ? Math.max(...locationZones.map(z => z.order ?? 0)) + 1
          : 0;

        await addDoc(collection(db, 'zones'), {
          name: zoneFormData.name,
          locationId: zoneFormData.locationId,
          locationName: selectedLocation?.name || '',
          description: zoneFormData.description,
          capacity: zoneFormData.capacity ? parseInt(zoneFormData.capacity) : null,
          isDisplay: zoneFormData.isDisplay,
          active: zoneFormData.active,
          order: maxOrder,
          createdAt: serverTimestamp(),
          createdBy: user.email
        });
        alert('Zone이 추가되었습니다.');
      }

      resetZoneForm();
      loadZones();
    } catch (error) {
      console.error('Zone 저장 오류:', error);
      alert('Zone 저장 중 오류가 발생했습니다.');
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
      description: location.description || '',
      capacity: location.capacity || '',
      manager: location.manager || '',
      contact: location.contact || '',
      active: location.active !== false
    });
    setShowAddModal(true);
  };

  const handleZoneEdit = (zone) => {
    setEditingZone(zone);
    setZoneFormData({
      name: zone.name,
      locationId: zone.locationId || '',
      locationName: zone.locationName || '',
      description: zone.description || '',
      capacity: zone.capacity || '',
      isDisplay: zone.isDisplay !== false,
      active: zone.active !== false
    });
    setShowZoneModal(true);
  };

  const handleDelete = async (location) => {
    if (location.itemCount > 0) {
      alert('해당 위치에 재고가 존재하여 삭제할 수 없습니다.\n먼저 재고를 다른 위치로 이동하세요.');
      return;
    }

    // 해당 위치에 속한 zone이 있는지 확인
    const locationZones = zones.filter(z => z.locationId === location.id);
    if (locationZones.length > 0) {
      alert('해당 위치에 Zone이 존재하여 삭제할 수 없습니다.\n먼저 Zone을 삭제하세요.');
      return;
    }

    if (!window.confirm(`"${location.name}" 위치를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'locations', location.id));
      alert('위치가 삭제되었습니다.');
      loadLocations();
    } catch (error) {
      console.error('위치 삭제 오류:', error);
      alert('위치 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleZoneDelete = async (zone) => {
    if (zone.itemCount > 0) {
      alert('해당 Zone에 재고가 존재하여 삭제할 수 없습니다.\n먼저 재고를 다른 Zone으로 이동하세요.');
      return;
    }

    if (!window.confirm(`"${zone.locationName} - ${zone.name}" Zone을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'zones', zone.id));
      alert('Zone이 삭제되었습니다.');
      loadZones();
    } catch (error) {
      console.error('Zone 삭제 오류:', error);
      alert('Zone 삭제 중 오류가 발생했습니다.');
    }
  };

  const toggleActive = async (location) => {
    try {
      const locationRef = doc(db, 'locations', location.id);
      await updateDoc(locationRef, {
        active: !location.active,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
      loadLocations();
    } catch (error) {
      console.error('상태 변경 오류:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  const toggleZoneActive = async (zone) => {
    try {
      const zoneRef = doc(db, 'zones', zone.id);
      await updateDoc(zoneRef, {
        active: !zone.active,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
      loadZones();
    } catch (error) {
      console.error('상태 변경 오류:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 위치 순서 변경 (위/아래 이동)
  const moveLocation = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    // 범위 체크
    if (newIndex < 0 || newIndex >= locations.length) return;

    try {
      const currentLocation = locations[index];
      const targetLocation = locations[newIndex];

      // 두 위치의 order 값을 교환
      const currentOrder = currentLocation.order ?? index;
      const targetOrder = targetLocation.order ?? newIndex;

      await Promise.all([
        updateDoc(doc(db, 'locations', currentLocation.id), {
          order: targetOrder,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        }),
        updateDoc(doc(db, 'locations', targetLocation.id), {
          order: currentOrder,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        })
      ]);

      loadLocations();
    } catch (error) {
      console.error('순서 변경 오류:', error);
      alert('순서 변경 중 오류가 발생했습니다.');
    }
  };

  // Zone 순서 변경 (같은 위치 내에서만)
  const moveZone = async (locationName, zoneIndex, direction) => {
    const locationZones = groupedZones[locationName];
    if (!locationZones) return;

    const newIndex = direction === 'up' ? zoneIndex - 1 : zoneIndex + 1;

    // 범위 체크
    if (newIndex < 0 || newIndex >= locationZones.length) return;

    try {
      const currentZone = locationZones[zoneIndex];
      const targetZone = locationZones[newIndex];

      // 두 zone의 order 값을 교환
      const currentOrder = currentZone.order ?? zoneIndex;
      const targetOrder = targetZone.order ?? newIndex;

      await Promise.all([
        updateDoc(doc(db, 'zones', currentZone.id), {
          order: targetOrder,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        }),
        updateDoc(doc(db, 'zones', targetZone.id), {
          order: currentOrder,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        })
      ]);

      loadZones();
    } catch (error) {
      console.error('Zone 순서 변경 오류:', error);
      alert('Zone 순서 변경 중 오류가 발생했습니다.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      description: '',
      capacity: '',
      manager: '',
      contact: '',
      active: true
    });
    setEditingLocation(null);
    setShowAddModal(false);
  };

  const resetZoneForm = () => {
    setZoneFormData({
      name: '',
      locationId: '',
      locationName: '',
      description: '',
      capacity: '',
      isDisplay: true,
      active: true
    });
    setEditingZone(null);
    setShowZoneModal(false);
  };

  // Zone을 위치별로 그룹화
  const groupedZones = zones.reduce((acc, zone) => {
    const locationName = zone.locationName || '미지정';
    if (!acc[locationName]) {
      acc[locationName] = [];
    }
    acc[locationName].push(zone);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">위치/Zone 관리</h2>
          <p className="text-gray-500 mt-1">
            {activeTab === 'locations'
              ? `총 ${locations.length}개 위치가 등록되어 있습니다.`
              : `총 ${zones.length}개 Zone이 등록되어 있습니다.`
            }
          </p>
        </div>
        <button
          onClick={() => activeTab === 'locations' ? setShowAddModal(true) : setShowZoneModal(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-lg transition shadow-md flex items-center font-medium"
        >
          <Icons.Plus className="w-5 h-5 mr-2" />
          {activeTab === 'locations' ? '위치 추가' : 'Zone 추가'}
        </button>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('locations')}
          className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'locations'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Icons.MapPin className="w-4 h-4 inline mr-2" />
          위치 ({locations.length})
        </button>
        <button
          onClick={() => setActiveTab('zones')}
          className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'zones'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Icons.Grid className="w-4 h-4 inline mr-2" />
          Zone ({zones.length})
        </button>
      </div>

      {/* 위치 탭 내용 */}
      {activeTab === 'locations' && (
        <>
          {/* 위치 목록 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location, index) => (
              <div key={location.id} className="bg-white rounded-xl shadow-sm border border-brand-100 p-6 hover:shadow-md transition-all duration-300 group">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-brand-500 bg-brand-50 px-2 py-0.5 rounded">{index + 1}</span>
                      <h3 className="text-xl font-bold text-brand-900 group-hover:text-brand-700 transition-colors">{location.name}</h3>
                    </div>
                    {location.address && (
                      <p className="text-sm text-gray-500 mt-1 flex items-center">
                        <Icons.MapPin className="w-3 h-3 mr-1" />
                        {location.address}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {/* 순서 변경 버튼 */}
                    <div className="flex flex-col mr-2">
                      <button
                        onClick={() => moveLocation(index, 'up')}
                        disabled={index === 0}
                        className={`p-1 rounded transition-colors ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-brand-600 hover:bg-brand-50'}`}
                        title="위로 이동"
                      >
                        <Icons.ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveLocation(index, 'down')}
                        disabled={index === locations.length - 1}
                        className={`p-1 rounded transition-colors ${index === locations.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-brand-600 hover:bg-brand-50'}`}
                        title="아래로 이동"
                      >
                        <Icons.ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => toggleActive(location)}
                      className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${location.active
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}
                    >
                      {location.active ? '활성' : '비활성'}
                    </button>
                  </div>
                </div>

                {location.description && (
                  <p className="text-sm text-gray-600 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    {location.description}
                  </p>
                )}

                <div className="space-y-3 text-sm mb-6">
                  <div className="flex justify-between items-center border-b border-gray-50 pb-2">
                    <span className="text-gray-500 flex items-center">
                      <Icons.Grid className="w-4 h-4 mr-2 text-gray-400" />
                      Zone 수
                    </span>
                    <span className="font-medium text-purple-600">
                      {zones.filter(z => z.locationId === location.id).length}개
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-50 pb-2">
                    <span className="text-gray-500 flex items-center">
                      <Icons.Users className="w-4 h-4 mr-2 text-gray-400" />
                      담당자
                    </span>
                    <span className="font-medium text-gray-900">{location.manager || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 flex items-center">
                      <Icons.Box className="w-4 h-4 mr-2 text-gray-400" />
                      현재 재고
                    </span>
                    <span className="font-bold text-brand-600 text-lg">{location.itemCount}개</span>
                  </div>
                </div>

                {location.capacity && (
                  <div className="mb-6">
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5 font-medium">
                      <span>수용률 ({location.capacity}개 중)</span>
                      <span>{Math.round((location.itemCount / location.capacity) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${(location.itemCount / location.capacity) > 0.9
                            ? 'bg-red-500'
                            : (location.itemCount / location.capacity) > 0.7
                              ? 'bg-yellow-500'
                              : 'bg-brand-500'
                          }`}
                        style={{ width: `${Math.min((location.itemCount / location.capacity) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handleEdit(location)}
                    className="flex-1 px-4 py-2 bg-white border border-brand-200 text-brand-700 hover:bg-brand-50 hover:border-brand-300 rounded-lg transition text-sm font-medium flex items-center justify-center"
                  >
                    <Icons.Edit className="w-4 h-4 mr-2" />
                    수정
                  </button>
                  <button
                    onClick={() => handleDelete(location)}
                    disabled={location.itemCount > 0 || zones.filter(z => z.locationId === location.id).length > 0}
                    className={`flex-1 px-4 py-2 rounded-lg transition text-sm font-medium flex items-center justify-center ${location.itemCount > 0 || zones.filter(z => z.locationId === location.id).length > 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                        : 'bg-white border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
                      }`}
                  >
                    <Icons.Trash className="w-4 h-4 mr-2" />
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>

          {locations.length === 0 && (
            <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
              <Icons.MapPin className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">등록된 위치가 없습니다</h3>
              <p className="text-gray-500 mb-6">새로운 재고 보관 위치를 등록하여 관리해보세요.</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-lg transition shadow-md font-medium"
              >
                첫 위치 등록하기
              </button>
            </div>
          )}
        </>
      )}

      {/* Zone 탭 내용 */}
      {activeTab === 'zones' && (
        <>
          {Object.keys(groupedZones).length > 0 ? (
            <div className="space-y-8">
              {Object.entries(groupedZones).map(([locationName, locationZones]) => (
                <div key={locationName}>
                  <h3 className="text-lg font-bold text-brand-900 mb-4 flex items-center">
                    <Icons.MapPin className="w-5 h-5 mr-2 text-brand-500" />
                    {locationName}
                    <span className="ml-2 text-sm font-normal text-gray-500">({locationZones.length}개 Zone)</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {locationZones.map((zone, zoneIndex) => (
                      <div key={zone.id} className="bg-white rounded-xl shadow-sm border border-brand-100 p-5 hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-purple-500 bg-purple-50 px-2 py-0.5 rounded">{zoneIndex + 1}</span>
                              <h4 className="text-lg font-bold text-brand-900">{zone.name}</h4>
                              {zone.isDisplay && (
                                <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded">전시</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Zone 순서 변경 버튼 */}
                            <div className="flex flex-col mr-1">
                              <button
                                onClick={() => moveZone(locationName, zoneIndex, 'up')}
                                disabled={zoneIndex === 0}
                                className={`p-0.5 rounded transition-colors ${zoneIndex === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'}`}
                                title="위로 이동"
                              >
                                <Icons.ChevronUp className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => moveZone(locationName, zoneIndex, 'down')}
                                disabled={zoneIndex === locationZones.length - 1}
                                className={`p-0.5 rounded transition-colors ${zoneIndex === locationZones.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'}`}
                                title="아래로 이동"
                              >
                                <Icons.ChevronDown className="w-3 h-3" />
                              </button>
                            </div>
                            <button
                              onClick={() => toggleZoneActive(zone)}
                              className={`px-2 py-0.5 rounded-full text-xs font-bold transition-colors ${zone.active
                                  ? 'bg-green-100 text-green-700 border border-green-200'
                                  : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}
                            >
                              {zone.active ? '활성' : '비활성'}
                            </button>
                          </div>
                        </div>

                        {zone.description && (
                          <p className="text-sm text-gray-600 mb-3 bg-gray-50 p-2 rounded border border-gray-100">
                            {zone.description}
                          </p>
                        )}

                        <div className="flex justify-between items-center text-sm mb-4">
                          <span className="text-gray-500">현재 재고</span>
                          <span className="font-bold text-brand-600">{zone.itemCount}개</span>
                        </div>

                        {zone.capacity && (
                          <div className="mb-4">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>수용률</span>
                              <span>{Math.round((zone.itemCount / zone.capacity) * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${(zone.itemCount / zone.capacity) > 0.9
                                    ? 'bg-red-500'
                                    : (zone.itemCount / zone.capacity) > 0.7
                                      ? 'bg-yellow-500'
                                      : 'bg-purple-500'
                                  }`}
                                style={{ width: `${Math.min((zone.itemCount / zone.capacity) * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        )}

                        <div className="flex space-x-2 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => handleZoneEdit(zone)}
                            className="flex-1 px-3 py-1.5 bg-white border border-brand-200 text-brand-700 hover:bg-brand-50 rounded-lg transition text-sm font-medium flex items-center justify-center"
                          >
                            <Icons.Edit className="w-3 h-3 mr-1" />
                            수정
                          </button>
                          <button
                            onClick={() => handleZoneDelete(zone)}
                            disabled={zone.itemCount > 0}
                            className={`flex-1 px-3 py-1.5 rounded-lg transition text-sm font-medium flex items-center justify-center ${zone.itemCount > 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                : 'bg-white border border-red-200 text-red-600 hover:bg-red-50'
                              }`}
                          >
                            <Icons.Trash className="w-3 h-3 mr-1" />
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
              <Icons.Grid className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">등록된 Zone이 없습니다</h3>
              <p className="text-gray-500 mb-6">위치 내 세부 구역(Zone)을 등록하여 관리해보세요.</p>
              <button
                onClick={() => setShowZoneModal(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-lg transition shadow-md font-medium"
              >
                첫 Zone 등록하기
              </button>
            </div>
          )}
        </>
      )}

      {/* 위치 추가/수정 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all scale-100">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
                <h2 className="text-2xl font-bold text-brand-900 flex items-center">
                  {editingLocation ? <Icons.Edit className="w-6 h-6 mr-2 text-brand-500" /> : <Icons.Plus className="w-6 h-6 mr-2 text-brand-500" />}
                  {editingLocation ? '위치 정보 수정' : '새 위치 추가'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <Icons.X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">
                    위치 이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="예: 자곡동 매장"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">주소</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="상세 주소를 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">설명</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="위치에 대한 설명을 입력하세요"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-brand-900 mb-2">수용 가능 수량</label>
                    <input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      placeholder="선택사항"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-brand-900 mb-2">담당자</label>
                    <input
                      type="text"
                      value={formData.manager}
                      onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      placeholder="담당자 이름"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">연락처</label>
                  <input
                    type="tel"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="010-0000-0000"
                  />
                </div>

                <div className="flex items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-5 h-5 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-3 text-sm font-medium text-gray-900 cursor-pointer">
                    이 위치를 활성 상태로 설정
                  </label>
                </div>

                <div className="flex space-x-3 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-6 py-3 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition font-medium"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-md font-bold"
                  >
                    {editingLocation ? '수정 완료' : '위치 추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Zone 추가/수정 모달 */}
      {showZoneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                <h2 className="text-xl font-bold text-brand-900 flex items-center">
                  {editingZone ? <Icons.Edit className="w-5 h-5 mr-2 text-brand-500" /> : <Icons.Plus className="w-5 h-5 mr-2 text-brand-500" />}
                  {editingZone ? 'Zone 수정' : '새 Zone 추가'}
                </h2>
                <button
                  onClick={resetZoneForm}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <Icons.X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleZoneSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">
                    상위 위치 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={zoneFormData.locationId}
                    onChange={(e) => setZoneFormData({ ...zoneFormData, locationId: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    required
                  >
                    <option value="">위치 선택</option>
                    {locations.filter(loc => loc.active).map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">
                    Zone 이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={zoneFormData.name}
                    onChange={(e) => setZoneFormData({ ...zoneFormData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="예: A구역, 입구쪽, 2층"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">설명</label>
                  <textarea
                    value={zoneFormData.description}
                    onChange={(e) => setZoneFormData({ ...zoneFormData, description: e.target.value })}
                    rows="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="Zone에 대한 설명"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-brand-900 mb-2">수용 가능 수량</label>
                  <input
                    type="number"
                    value={zoneFormData.capacity}
                    onChange={(e) => setZoneFormData({ ...zoneFormData, capacity: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="선택사항"
                  />
                </div>

                <div className="flex items-center gap-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isDisplay"
                      checked={zoneFormData.isDisplay}
                      onChange={(e) => setZoneFormData({ ...zoneFormData, isDisplay: e.target.checked })}
                      className="w-5 h-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isDisplay" className="ml-2 text-sm font-medium text-gray-900 cursor-pointer">
                      전시 구역
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="zoneActive"
                      checked={zoneFormData.active}
                      onChange={(e) => setZoneFormData({ ...zoneFormData, active: e.target.checked })}
                      className="w-5 h-5 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                    />
                    <label htmlFor="zoneActive" className="ml-2 text-sm font-medium text-gray-900 cursor-pointer">
                      활성 상태
                    </label>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={resetZoneForm}
                    className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition font-medium"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-md font-bold"
                  >
                    {editingZone ? '수정 완료' : 'Zone 추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationManagement;
